/**
 * Central logging utility for the application
 * Only displays logs in development mode
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  /** Component name for better traceability */
  component?: string;
  /** Function name for better traceability */
  function?: string;
  /** User ID for user-specific logs */
  userId?: string;
  /** Additional custom fields */
  [key: string]: any;
}

export interface ApiCallStatus {
  id: string;
  method: string;
  url: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status: 'pending' | 'success' | 'error';
  requestData?: any;
  responseData?: any;
  error?: any;
  component?: string;
}

class ApiTracker {
  private calls: Map<string, ApiCallStatus> = new Map();
  private listeners: Array<(calls: ApiCallStatus[]) => void> = [];

  /**
   * Start tracking an API call
   */
  startCall(method: string, url: string, data?: any, component?: string): string {
    const id = `${method}_${url}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const call: ApiCallStatus = {
      id,
      method: method.toUpperCase(),
      url,
      startTime: Date.now(),
      status: 'pending',
      requestData: data,
      component
    };
    
    this.calls.set(id, call);
    this.notifyListeners();
    return id;
  }

  /**
   * Mark an API call as completed successfully
   */
  completeCall(id: string, response?: any): void {
    const call = this.calls.get(id);
    if (call) {
      call.endTime = Date.now();
      call.duration = call.endTime - call.startTime;
      call.status = 'success';
      call.responseData = response;
      this.calls.set(id, call);
      this.notifyListeners();
    }
  }

  /**
   * Mark an API call as failed
   */
  failCall(id: string, error?: any): void {
    const call = this.calls.get(id);
    if (call) {
      call.endTime = Date.now();
      call.duration = call.endTime - call.startTime;
      call.status = 'error';
      call.error = error;
      this.calls.set(id, call);
      this.notifyListeners();
    }
  }

  /**
   * Get all tracked API calls
   */
  getCalls(): ApiCallStatus[] {
    return Array.from(this.calls.values()).sort((a, b) => b.startTime - a.startTime);
  }

  /**
   * Get pending API calls
   */
  getPendingCalls(): ApiCallStatus[] {
    return this.getCalls().filter(call => call.status === 'pending');
  }

  /**
   * Get completed API calls (last 10)
   */
  getCompletedCalls(): ApiCallStatus[] {
    return this.getCalls()
      .filter(call => call.status !== 'pending')
      .slice(0, 10);
  }

  /**
   * Clear old completed calls (keep last 10)
   */
  cleanup(): void {
    const allCalls = this.getCalls();
    const pendingCalls = allCalls.filter(call => call.status === 'pending');
    const completedCalls = allCalls.filter(call => call.status !== 'pending').slice(0, 10);
    
    this.calls.clear();
    [...pendingCalls, ...completedCalls].forEach(call => {
      this.calls.set(call.id, call);
    });
    
    this.notifyListeners();
  }

  /**
   * Subscribe to API call updates
   */
  subscribe(listener: (calls: ApiCallStatus[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getCalls()));
  }
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private apiTracker = new ApiTracker();
  
  /**
   * Get the API tracker instance
   */
  getApiTracker(): ApiTracker {
    return this.apiTracker;
  }

  /**
   * Formats a log message with timestamp and context
   * @param level - Log level
   * @param message - Log message
   * @param context - Optional context information
   * @returns Formatted message string
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString().split('T')[1].substring(0, 8);
    const prefix = context?.component ? `[${context.component}]` : '';
    const fnPrefix = context?.function ? `${context.function}:` : '';
    
    return `${timestamp} ${level.toUpperCase()} ${prefix} ${fnPrefix} ${message}`;
  }

  /**
   * Gets emoji icon for log level
   * @param level - Log level
   * @returns Emoji string
   */
  private getIcon(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🔍';
      case 'info': return 'ℹ️';
      case 'warn': return '⚠️';
      case 'error': return '❌';
      default: return '';
    }
  }

  /**
   * Logs debug information (development only)
   * @param message - Debug message
   * @param data - Optional data to log
   * @param context - Optional context information
   */
  debug(message: string, data?: any, context?: LogContext): void {
    if (!this.isDevelopment) return;
    
    const formattedMessage = this.formatMessage('debug', message, context);
    console.log(`🔍 ${formattedMessage}`, data || '');
  }

  /**
   * Logs informational messages (development only)
   * @param message - Info message
   * @param data - Optional data to log
   * @param context - Optional context information
   */
  info(message: string, data?: any, context?: LogContext): void {
    if (!this.isDevelopment) return;
    
    const formattedMessage = this.formatMessage('info', message, context);
    console.log(`ℹ️ ${formattedMessage}`, data || '');
  }

  /**
   * Logs warning messages (development only)
   * @param message - Warning message
   * @param data - Optional data to log
   * @param context - Optional context information
   */
  warn(message: string, data?: any, context?: LogContext): void {
    if (!this.isDevelopment) return;
    
    const formattedMessage = this.formatMessage('warn', message, context);
    console.warn(`⚠️ ${formattedMessage}`, data || '');
  }

  /**
   * Logs error messages (development only)
   * @param message - Error message
   * @param error - Optional error object or data
   * @param context - Optional context information
   */
  error(message: string, error?: any, context?: LogContext): void {
    if (!this.isDevelopment) return;
    
    const formattedMessage = this.formatMessage('error', message, context);
    console.error(`❌ ${formattedMessage}`, error || '');
  }

  /**
   * Groups related logs together (development only)
   * @param title - Group title
   * @param fn - Function that contains the grouped logs
   * @param context - Optional context information
   */
  group(title: string, fn: () => void, context?: LogContext): void {
    if (!this.isDevelopment) return;
    
    const formattedTitle = this.formatMessage('info', title, context);
    console.group(`📋 ${formattedTitle}`);
    fn();
    console.groupEnd();
  }

  /**
   * Specialized logging for user type debugging (development only)
   * @param data - User type debug data
   */
  userTypeDebug(data: {
    isAuthenticated: boolean;
    userId?: string | null;
    isCustomer: boolean;
    isFiliale: boolean;
    isLoading: boolean;
    userData?: any;
    redirectData?: any;
    recommendedRoute?: string;
  }): void {
    if (!this.isDevelopment) return;

    this.group('User Type Debug', () => {
      console.log('🔐 isAuthenticated:', data.isAuthenticated);
      console.log('👤 userId:', data.userId || 'null');
      console.log('⏳ isLoading:', data.isLoading);
      console.log('🛒 isCustomer:', data.isCustomer);
      console.log('🏢 isFiliale:', data.isFiliale);
      console.log('📊 userData:', data.userData);
      console.log('🔄 redirectData:', data.redirectData);
      console.log('🛤️ recommendedRoute:', data.recommendedRoute);
    });
  }

  /**
   * Logs API requests and responses with tracking (development only)
   * @param method - HTTP method
   * @param url - API endpoint URL
   * @param data - Request data
   * @param response - Response data
   * @param error - Error object if request failed
   * @param context - Optional context information
   */
  api(method: string, url: string, data?: any, response?: any, error?: any ): void {
    if (!this.isDevelopment) return;

    const status = error ? 'ERROR' : 'SUCCESS';
    const icon = error ? '❌' : '✅';
    
    console.group(`${icon} API ${method.toUpperCase()} ${url} - ${status}`);
    if (data) console.log('📤 Request:', data);
    if (response) console.log('📥 Response:', response);
    if (error) console.error('💥 Error:', error);
    console.groupEnd();
  }

  /**
   * Start tracking an API call and return tracking ID
   * @param method - HTTP method
   * @param url - API endpoint URL
   * @param data - Request data
   * @param context - Optional context information
   * @returns Tracking ID for this API call
   */
  apiStart(method: string, url: string, data?: any, context?: LogContext): string {
    if (!this.isDevelopment) return '';

    const trackingId = this.apiTracker.startCall(method, url, data, context?.component);
    
    this.group(`🚀 API ${method.toUpperCase()} ${url} - STARTED`, () => {
      if (data) console.log('📤 Request:', data);
      console.log('🆔 Tracking ID:', trackingId);
    });

    return trackingId;
  }

  /**
   * Complete tracking of an API call
   * @param trackingId - Tracking ID from apiStart
   * @param response - Response data
   * @param context - Optional context information
   */
  apiComplete(trackingId: string, response?: any ): void {
    if (!this.isDevelopment || !trackingId) return;

    this.apiTracker.completeCall(trackingId, response);
    const call = this.apiTracker.getCalls().find(c => c.id === trackingId);
    
    if (call) {
      this.group(`✅ API ${call.method} ${call.url} - COMPLETED (${call.duration}ms)`, () => {
        if (response) console.log('📥 Response:', response);
        console.log('⏱️ Duration:', `${call.duration}ms`);
      });
    }
  }

  /**
   * Mark an API call as failed
   * @param trackingId - Tracking ID from apiStart
   * @param error - Error object
   * @param context - Optional context information
   */
  apiFail(trackingId: string, error?: any): void {
    if (!this.isDevelopment || !trackingId) return;

    this.apiTracker.failCall(trackingId, error);
    const call = this.apiTracker.getCalls().find(c => c.id === trackingId);
    
    if (call) {
      this.group(`❌ API ${call.method} ${call.url} - FAILED (${call.duration}ms)`, () => {
        if (error) console.error('💥 Error:', error);
        console.log('⏱️ Duration:', `${call.duration}ms`);
      });
    }
  }

  /**
   * Logs form submissions (development only)
   * @param formName - Name of the form
   * @param data - Form data
   * @param success - Whether submission was successful
   * @param error - Error object if submission failed
   */
  form(formName: string, data: any, success: boolean, error?: any): void {
    if (!this.isDevelopment) return;

    const icon = success ? '✅' : '❌';
    const status = success ? 'SUCCESS' : 'ERROR';
    
    console.group(`${icon} Form ${formName} - ${status}`);
    console.log('📝 Data:', data);
    if (error) console.error('💥 Error:', error);
    console.groupEnd();
  }

  /**
   * Logs WebSocket events (development only)
   * @param event - Event name
   * @param data - Event data
   * @param direction - Event direction (incoming/outgoing)
   */
  socket(event: string, data?: any, direction: 'in' | 'out' = 'out'): void {
    if (!this.isDevelopment) return;

    const icon = direction === 'out' ? '📤' : '📥';
    console.log(`${icon} Socket ${event}`, data || '');
  }
}

/** Singleton logger instance */
export const logger = new Logger();

/**
 * Convenience functions for common logging use cases
 * All functions are no-ops in production
 */
export const log = {
  /** Log debug information */
  debug: (message: string, data?: any, context?: LogContext) => logger.debug(message, data, context),
  /** Log informational messages */
  info: (message: string, data?: any, context?: LogContext) => logger.info(message, data, context),
  /** Log warning messages */
  warn: (message: string, data?: any, context?: LogContext) => logger.warn(message, data, context),
  /** Log error messages */
  error: (message: string, error?: any, context?: LogContext) => logger.error(message, error, context),
  /** Group related logs */
  group: (title: string, fn: () => void, context?: LogContext) => logger.group(title, fn, context),
  /** Log user type debugging information */
  userType: (data: Parameters<typeof logger.userTypeDebug>[0]) => logger.userTypeDebug(data),
  /** Log API requests and responses */
  api: (method: string, url: string, data?: any, response?: any, error?: any) => logger.api(method, url, data, response, error),
  /** Start tracking an API call */
  apiStart: (method: string, url: string, data?: any, context?: LogContext) => logger.apiStart(method, url, data, context),
  /** Complete an API call */
  apiComplete: (trackingId: string, response?: any) => logger.apiComplete(trackingId, response),
  /** Mark API call as failed */
  apiFail: (trackingId: string, error?: any) => logger.apiFail(trackingId, error),
  /** Log form submissions */
  form: (formName: string, data: any, success: boolean, error?: any) => logger.form(formName, data, success, error),
  /** Log WebSocket events */
  socket: (event: string, data?: any, direction: 'in' | 'out' = 'out') => logger.socket(event, data, direction),
  /** Get API tracker for real-time monitoring */
  getApiTracker: () => logger.getApiTracker(),
}; 